import type {
  Breadcrumb,
  ErrorEvent,
  EventHint,
  Integration,
} from '@sentry/core';
import type * as SentryTypes from '@sentry/react';

let isInitialized = false;

let sentryModulePromise: Promise<typeof SentryTypes> | null = null;

let Sentry: typeof SentryTypes | null = null;

const loadSentry = (): Promise<typeof SentryTypes> => {
  if (!sentryModulePromise) {
    sentryModulePromise = import('@sentry/react').then(mod => {
      Sentry = mod;
      return mod;
    });
  }
  return sentryModulePromise;
};

const DEFAULT_TRACES_SAMPLE_RATE = 0.1;
// console.info from every prod session was pure quota burn; keep only levels
// that can carry an actionable problem.
const DEFAULT_LOG_LEVELS = ['warn', 'error'];

/**
 * Errors reported before the deferred Sentry init completes (boot crashes are
 * exactly the events we most want) are held here and flushed after init.
 * Null once init has resolved either way: initialized → drained, telemetry
 * disabled → dropped, so the buffer never grows for the app's lifetime.
 */
const MAX_PENDING_CAPTURES = 20;
let pendingCaptures: Array<{ error: Error; context: CaptureContext }> | null =
  [];

const parseBooleanEnv = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return fallback;
};

const parseSampleRate = (value: unknown, fallback: number): number => {
  const parsed = Number.parseFloat(String(value));
  if (Number.isNaN(parsed)) {
    return fallback;
  }
  if (parsed < 0 || parsed > 1) {
    return fallback;
  }
  return parsed;
};

const parseLogLevels = (value: unknown): string[] => {
  if (typeof value !== 'string') {
    return DEFAULT_LOG_LEVELS;
  }
  const parsed = value
    .split(',')
    .map(level => level.trim())
    .filter(Boolean);
  return parsed.length > 0 ? parsed : DEFAULT_LOG_LEVELS;
};

const getCurrentHostname = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location?.hostname || '';
};

const isLocalhostHostname = (hostname: string): boolean => {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname === ''
  );
};

export const shouldFilterError = (
  error: Error | { message?: string; stack?: string },
  source?: string
): boolean => {
  if (source && typeof source === 'string') {
    const lowerSource = source.toLowerCase();
    if (
      lowerSource.includes('connect.facebook.net') ||
      lowerSource.startsWith('iabjs://')
    ) {
      return true;
    }
  }

  if (error?.message && typeof error.message === 'string') {
    const message = error.message.toLowerCase();
    if (
      message.includes('facebook') ||
      message.includes('meta pixel') ||
      message.includes('fbq') ||
      message.includes('java object is gone') ||
      message.includes('enablebuttonsclickedmetadatalogging') ||
      // Facebook's iOS in-app browser (WKWebView) injects a script that posts
      // to the JS<->native bridge; when the handler is missing it throws
      // "...is not an object (evaluating 'window.webkit.messageHandlers[t].postMessage')"
      // against our own document. Not actionable from our side.
      message.includes('webkit.messagehandlers')
    ) {
      return true;
    }
  }

  if ('stack' in error && error?.stack && typeof error.stack === 'string') {
    const stack = error.stack.toLowerCase();
    if (stack.includes('connect.facebook.net') || stack.includes('iabjs://')) {
      return true;
    }
  }

  return false;
};

export const initializeSentry = async (): Promise<boolean> => {
  if (isInitialized) {
    return true;
  }

  try {
    const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
    const environment =
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) ||
      (import.meta.env.PROD ? 'production' : 'development');
    const tracesSampleRate = parseSampleRate(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      DEFAULT_TRACES_SAMPLE_RATE
    );
    const logLevels = parseLogLevels(import.meta.env.VITE_SENTRY_LOG_LEVELS);
    const enableLogs = parseBooleanEnv(
      import.meta.env.VITE_SENTRY_ENABLE_LOGS,
      true
    );
    const isEnabled = parseBooleanEnv(
      import.meta.env.VITE_SENTRY_ENABLED,
      import.meta.env.PROD
    );
    const allowLocalhost = parseBooleanEnv(
      import.meta.env.VITE_SENTRY_ALLOW_LOCALHOST,
      false
    );
    const hostname = getCurrentHostname();
    const isLocalhost = isLocalhostHostname(hostname);
    const shouldSendTelemetry = isEnabled && (!isLocalhost || allowLocalhost);

    if (!dsn || typeof dsn !== 'string' || dsn.trim() === '') {
      if (isEnabled && !isLocalhost) {
        console.warn(
          '[Sentry] VITE_SENTRY_DSN is missing; telemetry is disabled.'
        );
      }
      pendingCaptures = null;
      return false;
    }

    if (!shouldSendTelemetry) {
      pendingCaptures = null;
      return false;
    }

    const SentryModule = await loadSentry();

    const integrations: Integration[] = [];

    if (SentryModule.browserTracingIntegration) {
      integrations.push(SentryModule.browserTracingIntegration());
    }

    // Replay only when an error occurs — recording 10% of ALL sessions was
    // the single biggest quota drain and almost never got watched.
    if (SentryModule.replayIntegration) {
      integrations.push(
        SentryModule.replayIntegration({
          sessionSampleRate: 0,
          errorSampleRate: 1.0,
        } as Record<string, unknown>)
      );
    }

    if (enableLogs && SentryModule.consoleLoggingIntegration) {
      integrations.push(
        SentryModule.consoleLoggingIntegration({
          levels: logLevels,
        } as Record<string, unknown>)
      );
    }

    interface SentryLog {
      attributes?: Record<string, unknown>;
      [key: string]: unknown;
    }

    const addRouteAttribute = (log: SentryLog): SentryLog => {
      if (typeof window === 'undefined') {
        return log;
      }
      const attributes = { ...(log.attributes || {}) };
      if (!attributes.route && window.location?.pathname) {
        attributes.route = window.location.pathname;
      }
      return { ...log, attributes };
    };

    SentryModule.init({
      dsn,
      environment,
      integrations: integrations.length > 0 ? integrations : undefined,
      tracesSampleRate,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      // enableLogs/enableMetrics are both still experimental SDK flags. Metrics
      // happen to default on (`enableMetrics ?? _experiments?.enableMetrics ??
      // true`) in the current SDK, but that default is slated to change
      // (SDK `todo(v11)`), so pin it explicitly rather than depend on it.
      _experiments: { enableLogs, enableMetrics: true } as Record<
        string,
        unknown
      >,
      release: (import.meta.env.VITE_APP_VERSION as string) || undefined,

      beforeSend(event: ErrorEvent, hint: EventHint) {
        if (!shouldSendTelemetry) {
          return null;
        }

        const error = (hint.originalException || hint.syntheticException) as
          | Error
          | undefined;
        const source =
          event.request?.url ||
          event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename;

        // In-app browsers (Facebook/Instagram WKWebView) inject scripts that
        // throw against our own document, so the throwable isn't always a real
        // Error instance and its frames point at our URL, not a vendor script.
        // Fall back to the parsed exception type/value so the message filters
        // in shouldFilterError still match (e.g. window.webkit.messageHandlers).
        const exceptionValue = event.exception?.values?.[0];
        const candidate: { message?: string; stack?: string } = error ?? {
          message: [exceptionValue?.type, exceptionValue?.value]
            .filter(Boolean)
            .join(': '),
        };

        if (shouldFilterError(candidate, source)) {
          return null;
        }

        return event;
      },

      beforeSendLog(log: SentryLog) {
        if (!shouldSendTelemetry) {
          return null;
        }
        return addRouteAttribute(log);
      },

      beforeSendMetric(metric: unknown) {
        if (!shouldSendTelemetry) {
          return null;
        }
        return metric;
      },

      beforeBreadcrumb(breadcrumb: Breadcrumb) {
        if (
          breadcrumb.category === 'console' &&
          breadcrumb.message &&
          typeof breadcrumb.message === 'string'
        ) {
          const message = breadcrumb.message.toLowerCase();
          if (
            message.includes('facebook') ||
            message.includes('meta pixel') ||
            message.includes('fbq') ||
            message.startsWith('fbnavinp:')
          ) {
            return null;
          }
        }

        return breadcrumb;
      },
    } as unknown as Parameters<typeof SentryModule.init>[0]);

    isInitialized = true;

    // Flush anything captured before the deferred init finished (boot-time
    // crashes, early ErrorBoundary hits).
    const buffered = pendingCaptures ?? [];
    pendingCaptures = null;
    for (const { error, context } of buffered) {
      captureException(error, context);
    }

    return true;
  } catch (error) {
    console.warn('Failed to initialize Sentry:', error);
    return false;
  }
};

interface CaptureContext {
  level?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

export const captureException = (
  error: Error,
  context: CaptureContext = {}
): void => {
  if (!isInitialized || !Sentry) {
    if (pendingCaptures && pendingCaptures.length < MAX_PENDING_CAPTURES) {
      pendingCaptures.push({ error, context });
    }
    if (!import.meta.env.PROD) {
      console.error('Sentry not initialized, error logged to console:', error);
    }
    return;
  }

  try {
    if (shouldFilterError(error)) {
      return;
    }

    Sentry.captureException(error, {
      level: (context.level || 'error') as SentryTypes.SeverityLevel,
      tags: context.tags || {},
      extra: context.extra || {},
    });
  } catch (sentryError) {
    console.warn('Failed to capture exception in Sentry:', sentryError);
  }
};

export const captureMessage = (
  message: string,
  context: CaptureContext = {}
): void => {
  if (!isInitialized || !Sentry) {
    if (!import.meta.env.PROD) {
      console.warn(
        'Sentry not initialized, message logged to console:',
        message
      );
    }
    return;
  }

  try {
    Sentry.captureMessage(message, {
      level: (context.level || 'info') as SentryTypes.SeverityLevel,
      tags: context.tags || {},
      extra: context.extra || {},
    });
  } catch (sentryError) {
    console.warn('Failed to capture message in Sentry:', sentryError);
  }
};

export const addBreadcrumb = (
  message: string,
  data: Record<string, unknown> = {},
  category = 'custom',
  level: SentryTypes.SeverityLevel = 'info'
): void => {
  if (!isInitialized || !Sentry) {
    return;
  }

  try {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data,
      timestamp: Date.now() / 1000,
    });
  } catch (error) {
    console.warn('Failed to add breadcrumb to Sentry:', error);
  }
};

/**
 * The lazily-imported `@sentry/react` module, or null until deferred init has
 * resolved. Exposed so sibling telemetry services (e.g. metrics) reuse this
 * single loaded copy instead of firing their own `import('@sentry/react')`,
 * which races init and silently drops the first events emitted right after boot.
 */
export const getSentryModule = (): typeof SentryTypes | null => Sentry;

export const isSentryInitialized = (): boolean => {
  return isInitialized;
};
